import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { MdVpnLock } from "react-icons/md";
import { useProfileStore } from "../../store/useUserStore";
import { BiSolidCheckShield } from "react-icons/bi";
import { toast } from "react-toastify";

const VerifyChild = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, setLoading, submitParentActivate, parentActivateInfo } =
    useProfileStore();
  const [childrenNRIC, setChildrenNRIC] = useState<{ [key: number]: string[] }>(
    () => {
      const initial: { [key: number]: string[] } = {};
      parentActivateInfo &&
        parentActivateInfo.children.forEach((child: any) => {
          initial[child.student_id] = ["", "", "", ""];
        });
      return initial;
    },
  );
  const inputRefs = useRef<{ [key: string]: HTMLInputElement[] }>({});

  // const handleDigitChange = (
  //   studentId: number,
  //   index: number,
  //   value: string,
  // ) => {
  //   // Only allow single digit
  //   const digit = value.slice(-1).replace(/[^0-9]/g, "");

  //   setChildrenNRIC((prev) => ({
  //     ...prev,
  //     [studentId]: prev[studentId].map((d, i) => (i === index ? digit : d)),
  //   }));

  //   // Auto-focus next input
  //   if (digit && index < 3) {
  //     const nextInput = inputRefs.current[studentId]?.[index + 1];
  //     nextInput?.focus();
  //   }
  // };

  const handleChange = (
    studentId: string,
    index: number,
    value: string,
  ) => {
    const lettersAndDigits = value.slice(-1).replace(/[^a-zA-Z0-9]/g, "");

    setChildrenNRIC((prev) => ({
      ...prev,
      [studentId]: prev[studentId].map((d, i) => (i === index ? lettersAndDigits : d)),
    }));

    // Auto-focus next input
    if (lettersAndDigits && index < 3) {
      const nextInput = inputRefs.current[studentId]?.[index + 1];
      nextInput?.focus();
    }
  };

  const handleKeyDown = (
    studentId: number,
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (!childrenNRIC[studentId][index] && index > 0) {
        // Move to previous input on backspace if current is empty
        const prevInput = inputRefs.current[studentId]?.[index - 1];
        prevInput?.focus();
      } else if (childrenNRIC[studentId][index]) {
        // Clear current input
        setChildrenNRIC((prev) => ({
          ...prev,
          [studentId]: prev[studentId].map((d, i) => (i === index ? "" : d)),
        }));
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      const prevInput = inputRefs.current[studentId]?.[index - 1];
      prevInput?.focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      const nextInput = inputRefs.current[studentId]?.[index + 1];
      nextInput?.focus();
    }
  };

  const handlePaste = (
    studentId: string,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/[^a-zA-Z0-9]/g, "");
    const lettersAndDigits = pastedData.slice(4).split("");

    if (lettersAndDigits.length > 0) {
      setChildrenNRIC((prev) => ({
        ...prev,
        [studentId]: [
          lettersAndDigits[0] || "",
          lettersAndDigits[1] || "",
          lettersAndDigits[2] || "",
          lettersAndDigits[3] || "",
        ],
      }));

      // Focus last filled input or last input
      const focusIndex = Math.min(lettersAndDigits.length, 3);
      const targetInput = inputRefs.current[studentId]?.[focusIndex];
      targetInput?.focus();
    }
  };

  const handleVerify = async () => {
    // Collect and verify NRIC digits
    const children = parentActivateInfo?.children || [];
    const verificationData = children.map((child: any) => ({
      id: child.student_id,
      ic_last4: childrenNRIC[child.student_id].join(""),
    }));

    const incompleteChild = children.find((child: any) => {
      const ic_last4 = childrenNRIC[child.student_id].join("");
      return ic_last4.length !== 4;
    });

    if (incompleteChild) {
      toast.error(`Please enter all 4 digits for ${incompleteChild.name}`);
      return;
    }

    const payload = {
      mode: "verify_child",
      children: verificationData,
    };
    try {
      const result = await submitParentActivate(payload, "verify");
      if (result.success) {
        navigate("/activate/register");
      }
      if (result.data.code === 405) {
        navigate("/login#activate");
      }
    } catch (error) {
      console.error("Activation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!parentActivateInfo || !parentActivateInfo.verified_parent) {
      navigate("/login#activate");
    }
  }, []);

  return (
    <div className="pt-4 px-4 h-screen flex flex-col">
      <header className="pt-4 px-0 flex flex-col gap-3 items-start">
        <Link to="/" className="inline-flex">
          <img src="/assets/img/koku_small.png" className="h-7" alt="logo" />
        </Link>

        <Link to="/login" className="inline-flex">
          <IoMdArrowRoundBack className="text-2xl" />
        </Link>
      </header>
      <div className="px-0 flex-grow flex flex-col">
        {parentActivateInfo && parentActivateInfo.verified_parent && (
          <>
            <h3 className="text-4xl font-bold text-black self-center mt-5 mb-8">
              {t("activate.verify_your_account")}
            </h3>

            <div className="mb-7 border rounded-xl p-4 bg-[#004aad] text-white flex flex-col items-center justify-center self-center w-full py-8">
              <MdVpnLock className="text-3xl my-3" />
              <p>
                {t("activate.parentName")}: {parentActivateInfo.parent_name}
              </p>
              <p>NRIC: {parentActivateInfo.parent_ic}</p>
            </div>

            <div className="flex-grow overflow-y-auto">
              {parentActivateInfo &&
                parentActivateInfo.children.map(
                  (child: any, childIndex: number) => (
                    <div
                      key={child.student_id}
                      className="mb-7 border rounded-xl p-4 border-gray-400 text-white flex flex-col w-full py-8"
                    >
                      <h4 className="text-lg font-semibold text-black ml-12">
                        {t("activate.child")}
                        {childIndex + 1}: {child.name}
                      </h4>

                      <div className="mb-4">
                        <label className="text-sm font-medium text-gray-600 block mb-3 ml-12">
                          {t("activate.inputLast4Digits")}
                        </label>
                        <div className="flex gap-3 justify-center">
                          {[0, 1, 2, 3].map((index) => (
                            <input
                              key={index}
                              ref={(el) => {
                                if (!inputRefs.current[child.student_id]) {
                                  inputRefs.current[child.student_id] = [];
                                }
                                if (el) {
                                  inputRefs.current[child.student_id][index] =
                                    el;
                                }
                              }}
                              type="text"
                              maxLength={1}
                              value={
                                childrenNRIC[child.student_id]?.[index] || ""
                              }
                              onChange={(e) =>
                                handleChange(child.student_id, index, e.target.value)
                              }
                              onKeyDown={(e) =>
                                handleKeyDown(child.student_id, index, e)
                              }
                              onPaste={(e) => handlePaste(child.student_id, e)}
                              className="w-14 h-16 text-center text-2xl font-bold text-black border-2 border-gray-300 focus:border-theme outline-none rounded-lg transition-colors"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ),
                )}
            </div>
            <div className="mt-auto">
              <button
                type="submit"
                disabled={loading}
                className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center mb-4"
                onClick={handleVerify}
              >
                {loading ? (
                  "Loading..."
                ) : (
                  <>
                    <BiSolidCheckShield className="mx-1 text-xl" />
                    {t("activate.verify")}
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyChild;
